import { Core } from "@faire/design-tokens";
import { convertErrorToLoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { EmailSentScenario } from "@faire/retailer-visitor-shared/components/AppSignIn/components/EmailSentWaitingView";
import { AllResult } from "@faire/retailer-visitor-shared/components/AppSignIn/components/SignInController";
import { View } from "@faire/retailer-visitor-shared/components/AppSignIn/components/View";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { ErrorMessage } from "@faire/web--source/messenger/components/ErrorMessage";
import { Button } from "@faire/web--source/slate/Button";
import { Column, HorizontalLine } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import login from "@faire/web-api--source/endpoints/www/api/v2/users/login/post";
import { trackSigninCheckEmailEnterCodeClick } from "@faire/web-api--source/events/signin/click/checkEmailEnterCode";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { ILoginResponse } from "@faire/web-api--source/indigofair/data/ILoginResponse";
import { LoginState } from "@faire/web-api--source/indigofair/data/LoginState";
import { MfaMethod } from "@faire/web-api--source/indigofair/data/MfaMethod";
import * as React from "react";
import type { JSX } from "react";

export const CommonFooter: React.FC<{
  email: string;
  scenario: EmailSentScenario;
  onComplete: (result: AllResult) => void;
  password?: string;
}> = ({ onComplete, email, scenario, password }) => {
  return (
    <>
      <HorizontalLine />
      <Spacer size="3x" />
      <Typography color={Core.text.subdued}>
        <StrictLocalMsg
          defaultMessage="Using another device to sign in? <LinkFormatting>Enter code</LinkFormatting>"
          description={{
            text: "Prompt for the user to enter a code if they are using another device to sign in",
          }}
          values={{
            LinkFormatting: LinkFormatting(() => {
              trackSigninCheckEmailEnterCodeClick(scenario);
              onComplete(
                scenario === EmailSentScenario.AUTO
                  ? {
                      nextView: View.ENTER_CODE,
                      email,
                    }
                  : {
                      nextView: View.ENTER_MFA_CODE,
                      email,
                      mfaMethod: MfaMethod.EMAIL_ADDRESS,
                      mfaTitle: "",
                      mfaDescription: "",
                      password: password,
                    }
              );
            }),
          }}
        />
      </Typography>
    </>
  );
};

const LinkFormatting = (onClick: () => void) =>
  function (chunks: JSX.Element) {
    return (
      <Button variant="tertiary" onClick={onClick} tertiaryColor="inherit">
        {chunks}
      </Button>
    );
  };

const OptionBlock: React.FC<{
  title: string | undefined;
  method: string | undefined;
  onClick: () => void;
}> = ({ title, method, onClick }) => {
  return (
    <Column
      onClick={onClick}
      className="border-core-border-muted cursor-pointer rounded-lg border border-1 px-6 py-5"
    >
      <Typography>{title}</Typography>
      <Typography
        color={Core.text.subdued}
        style={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {method}
      </Typography>
    </Column>
  );
};

export const MFAOptionsFooter: React.FC<{
  email: string;
  scenario: EmailSentScenario;
  onComplete: (result: AllResult) => void;
  userToken?: string;
  password?: string;
  mfaOptionsMemoized?: ILoginResponse.IMfaOption[];
}> = ({
  email,
  scenario,
  onComplete,
  mfaOptionsMemoized,
  password,
  userToken,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { isActive, ssoToken } = useSSOStore();
  const [error, setError] = React.useState<string | undefined>(undefined);
  if (!mfaOptionsMemoized) {
    return (
      <CommonFooter email={email} scenario={scenario} onComplete={onComplete} />
    );
  }

  const getName = (method: string | undefined) => {
    if (method === "EMAIL_ADDRESS") {
      return strictLocalize({
        defaultMessage: "Email me a link",
        description: {
          text: "Button text for email option",
          img: "https://cdn.faire.com/fastly/781262f277b3bccd254eaae2433d6c841d0b96c0a179740be82a45df975b2226.png",
        },
      });
    } else if (method === "SMS") {
      return strictLocalize({
        defaultMessage: "Text me a code",
        description: {
          text: "Button text for text option",
          img: "https://cdn.faire.com/fastly/781262f277b3bccd254eaae2433d6c841d0b96c0a179740be82a45df975b2226.png",
        },
      });
    }
    return "";
  };

  const onClick = async (
    value: string | undefined,
    method: string | undefined
  ) => {
    if (!method || !value) {
      return;
    }
    try {
      const request = isActive
        ? ILoginRequest.build({
            mfa_method: method as MfaMethod,
            sso_token: ssoToken,
            password,
            enable_mfa_method_selection: true,
          })
        : ILoginRequest.build({
            password,
            email_address: email,
            user_token: !email ? userToken : undefined,
            mfa_method: method as MfaMethod,
            enable_mfa_method_selection: true,
          });

      const { mfa_title, mfa_description, login_state } = await login(request);

      if (login_state === LoginState.MFA_REQUIRED) {
        if (method === MfaMethod.SMS) {
          return onComplete?.({
            nextView: View.ENTER_MFA_CODE,
            email: email,
            userToken: userToken,
            mfaMethod: method,
            mfaTitle: mfa_title,
            mfaDescription: mfa_description,
            password: password,
          });
        } else if (method === MfaMethod.EMAIL_ADDRESS) {
          return onComplete?.({
            nextView: View.MFA_OPTIONS_EMAIL_SENT,
            email: email,
            mfaTitle: mfa_title,
            mfaDescription: mfa_description,
            password: password,
          });
        }
      }
    } catch (error) {
      setError(convertErrorToLoginError(error, strictLocalize)?.message);
    }
  };

  return (
    <Column gap="1x">
      {mfaOptionsMemoized.map(({ value, method }) => (
        <OptionBlock
          title={getName(method)}
          method={value}
          onClick={() => (method ? onClick(value, method) : undefined)}
          key={method}
        />
      ))}
      {error ? <ErrorMessage message={error} /> : null}
    </Column>
  );
};
